import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * The schedule + macro-stages + time tracker, Gantt as a single full-width
 * hero, with a horizontal macro-stages word-cloud band beneath it and a
 * compact 2-col (time tracker | hours report) row. No 1+3 pattern.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`. The English stage
 * list is the US/UK sequence: no ART/RRT filing or CAU registration, which
 * are Brazilian statutory steps, and permit/zoning review in their place.
 */

type Phase = { len: number; name: string; start: number; tone: string }
type Stage = { label: string; size: 'sm' | 'md' | 'lg' | 'xl' }

type TimelineContent = {
  eyebrow: string
  heading: string
  intro: string
  mockupTitle: string
  durationBadge: string
  timerBadge: string
  months: readonly string[]
  phases: readonly Phase[]
  stagesEyebrow: string
  stagesNote: string
  stages: readonly Stage[]
  stagesMore: string
  timer: {
    eyebrow: string
    task: string
    startedAt: string
    stats: readonly { l: string; v: string }[]
  }
  hours: {
    eyebrow: string
    total: string
    rows: readonly { h: number; label: string; w: number }[]
    exportLabel: string
    exportOptions: readonly string[]
  }
}

const CONTENT: Record<MarketingLocale, TimelineContent> = {
  en: {
    durationBadge: '90 days',
    eyebrow: 'SCHEDULE',
    heading: 'The architecture project, in the vocabulary of architecture',
    hours: {
      exportLabel: 'EXPORT',
      exportOptions: ['CSV', 'PDF', 'by project', 'by phase'],
      eyebrow: 'HOURS THIS MONTH · BY PERSON',
      rows: [
        { h: 38, label: 'Maya', w: 76 },
        { h: 28, label: 'Ryan', w: 56 },
        { h: 14, label: 'Chloe', w: 28 },
        { h: 22, label: 'Ben', w: 44 },
      ],
      total: '156 hours logged',
    },
    intro:
      'More than 40 project phases already loaded, a schedule that sees the whole year, and a quiet timer that feeds hours back into each project.',
    mockupTitle: 'Schedule · Whitfield House',
    months: ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'],
    phases: [
      { len: 8, name: 'Site survey', start: 0, tone: '#7bb86b' },
      { len: 12, name: 'Concept design', start: 8, tone: '#5b8def' },
      { len: 16, name: 'Permit set · zoning', start: 18, tone: '#e8a13d' },
      { len: 22, name: 'Construction documents', start: 30, tone: '#7a6cb6' },
      { len: 10, name: 'Consultant coordination', start: 48, tone: '#d96fa3' },
      { len: 30, name: 'Construction administration', start: 56, tone: '#1d7a4c' },
    ],
    stages: [
      { label: 'Site survey', size: 'lg' },
      { label: 'Pre-brief', size: 'sm' },
      { label: 'Concept design', size: 'lg' },
      { label: '3D massing study', size: 'md' },
      { label: 'Program of requirements', size: 'sm' },
      { label: 'Schematic design', size: 'md' },
      { label: 'Permit set', size: 'lg' },
      { label: 'Planning approval', size: 'md' },
      { label: 'Fire review', size: 'sm' },
      { label: 'Construction documents', size: 'xl' },
      { label: 'Millwork detailing', size: 'md' },
      { label: 'Lighting detailing', size: 'md' },
      { label: 'Finishes schedule', size: 'sm' },
      { label: 'Consultant coordination', size: 'lg' },
      { label: 'Outline specification', size: 'md' },
      { label: 'Bid and negotiation', size: 'md' },
      { label: 'Contractor selection', size: 'sm' },
      { label: 'Construction administration', size: 'xl' },
      { label: 'Periodic site visits', size: 'md' },
      { label: 'Final walkthrough', size: 'md' },
      { label: 'Owner’s manual', size: 'sm' },
      { label: 'Rendering', size: 'sm' },
    ],
    stagesEyebrow: 'BUILT-IN PROJECT PHASES',
    stagesMore: '+ 22 more',
    stagesNote: 'edit, reorder, hide — they’re yours',
    timer: {
      eyebrow: 'TIMER · MAYA',
      startedAt: 'started at 1:42 PM · today',
      stats: [
        { l: 'this week', v: '38h' },
        { l: 'on the project', v: '164h' },
        { l: 'of budget', v: '78%' },
      ],
      task: 'Detail the millwork · Whitfield House',
    },
    timerBadge: '▶ 02:18:42 · Maya',
  },
  'pt-BR': {
    durationBadge: '90 dias',
    eyebrow: 'CRONOGRAMA',
    heading: 'O projeto da arquitetura, com o vocabulário da arquitetura',
    hours: {
      exportLabel: 'EXPORTAR',
      exportOptions: ['CSV', 'PDF', 'por projeto', 'por etapa'],
      eyebrow: 'HORAS DO MÊS · POR PESSOA',
      rows: [
        { h: 38, label: 'Marina', w: 76 },
        { h: 28, label: 'Rafael', w: 56 },
        { h: 14, label: 'Camila', w: 28 },
        { h: 22, label: 'Bruno', w: 44 },
      ],
      total: '156 horas lançadas',
    },
    intro:
      'Mais de 40 macroetapas já carregadas, cronograma que enxerga o ano inteiro e um timer discreto que alimenta as horas por projeto.',
    mockupTitle: 'Cronograma · Residência Lopes',
    months: ['mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov'],
    phases: [
      { len: 8, name: 'Levantamento', start: 0, tone: '#7bb86b' },
      { len: 12, name: 'Estudo preliminar', start: 8, tone: '#5b8def' },
      { len: 16, name: 'Projeto legal · alvará', start: 18, tone: '#e8a13d' },
      { len: 22, name: 'Projeto executivo', start: 30, tone: '#7a6cb6' },
      { len: 10, name: 'Compatibilização', start: 48, tone: '#d96fa3' },
      { len: 30, name: 'Acompanhamento de obra', start: 56, tone: '#1d7a4c' },
    ],
    stages: [
      { label: 'Levantamento', size: 'lg' },
      { label: 'Pré-briefing', size: 'sm' },
      { label: 'Estudo preliminar', size: 'lg' },
      { label: 'Estudo de volumetria 3D', size: 'md' },
      { label: 'Programa de necessidades', size: 'sm' },
      { label: 'Anteprojeto', size: 'md' },
      { label: 'Projeto legal', size: 'lg' },
      { label: 'Aprovação na prefeitura', size: 'md' },
      { label: 'Bombeiros', size: 'sm' },
      { label: 'Projeto executivo', size: 'xl' },
      { label: 'Detalhamento de marcenaria', size: 'md' },
      { label: 'Detalhamento de iluminação', size: 'md' },
      { label: 'Detalhamento de pintura', size: 'sm' },
      { label: 'Compatibilização', size: 'lg' },
      { label: 'Memorial descritivo', size: 'md' },
      { label: 'Emissão de ART/RRT', size: 'md' },
      { label: 'Registro no CAU', size: 'sm' },
      { label: 'Acompanhamento de obra', size: 'xl' },
      { label: 'Visitas técnicas periódicas', size: 'md' },
      { label: 'Vistoria final', size: 'md' },
      { label: 'Manual do proprietário', size: 'sm' },
      { label: 'Renderização', size: 'sm' },
    ],
    stagesEyebrow: 'MACROETAPAS NATIVAS',
    stagesMore: '+ outras 22',
    stagesNote: 'edite, reordene, esconda, é seu',
    timer: {
      eyebrow: 'TIMER · MARINA',
      startedAt: 'iniciado às 13:42 · hoje',
      stats: [
        { l: 'esta semana', v: '38h' },
        { l: 'no projeto', v: '164h' },
        { l: 'do orçado', v: '78%' },
      ],
      task: 'Detalhar marcenaria · Residência Lopes',
    },
    timerBadge: '▶ 02:18:42 · Marina',
  },
}

const SIZE_CLASS: Record<string, string> = {
  lg: 'text-[15px] font-semibold text-[#3e3e44]',
  md: 'text-[13px] text-[#6c6c72]',
  sm: 'text-[11.5px] text-[#9a9aa0]',
  xl: 'text-[18px] font-semibold text-[#1c1c20]',
}

const TimelineMockup = ({ content }: { content: TimelineContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(60,40,20,0.28)]">
    <div className="flex items-center justify-between border-[#f1f1f1] border-b px-5 py-3">
      <span className="font-semibold text-[#1c1c20] text-[13px]">{content.mockupTitle}</span>
      <div className="flex items-center gap-2">
        <span className="rounded bg-[#f1f1f1] px-2 py-1 text-[#6c6c72] text-[10.5px]">
          {content.durationBadge}
        </span>
        <span className="rounded bg-[#fef3e0] px-2 py-1 font-medium text-[#a87a18] text-[10.5px]">
          {content.timerBadge}
        </span>
      </div>
    </div>
    <div className="px-5 py-4">
      <div className="grid grid-cols-9 gap-0 pb-2 text-[#9a9aa0] text-[10px]">
        {content.months.map((m) => (
          <span key={m}>{m}</span>
        ))}
      </div>
      <div className="space-y-2">
        {content.phases.map((phase) => (
          <div className="grid grid-cols-[1.6fr_3fr] items-center gap-3" key={phase.name}>
            <span className="truncate text-[#43434a] text-[11.5px]">{phase.name}</span>
            <div className="relative h-5 rounded-md bg-[#fafafa]">
              <span
                className="absolute top-0 bottom-0 rounded-md"
                style={{
                  background: phase.tone,
                  left: `${phase.start}%`,
                  width: `${phase.len}%`,
                }}
              />
              <span
                aria-hidden="true"
                className="absolute top-0 bottom-0 left-[40%] w-px bg-[#1c1c20]/40"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const Timer = ({ content }: { content: TimelineContent }) => (
  <div className="rounded-2xl border border-[#ece6d8] bg-[#1c1c20] p-6 text-white">
    <p className="text-[#fde9b8] text-[10.5px] tracking-[0.2em]">{content.timer.eyebrow}</p>
    <p className="mt-1 text-[11.5px] text-white/60">{content.timer.task}</p>

    <div className="mt-5 flex items-center gap-4">
      <span className="flex size-14 items-center justify-center rounded-full bg-[#a87a18] font-bold text-[16px]">
        ▶
      </span>
      <div>
        <p className="font-bold font-mono text-[34px] leading-none">02:18:42</p>
        <p className="mt-1 text-[11px] text-white/60">{content.timer.startedAt}</p>
      </div>
    </div>

    <div className="mt-6 grid grid-cols-3 gap-2">
      {content.timer.stats.map((s) => (
        <div className="rounded-md bg-white/[0.06] px-2.5 py-2" key={s.l}>
          <p className="font-semibold text-[14px]">{s.v}</p>
          <p className="text-[10px] text-white/60">{s.l}</p>
        </div>
      ))}
    </div>
  </div>
)

const HoursReport = ({ content }: { content: TimelineContent }) => (
  <div className="rounded-2xl border border-[#ece6d8] bg-white p-6">
    <p className="text-[#a87a18] text-[10.5px] tracking-[0.2em]">{content.hours.eyebrow}</p>
    <p className="mt-1 font-bold text-[#0b0b0d] text-[18px]">{content.hours.total}</p>

    <div className="mt-5 space-y-3">
      {content.hours.rows.map((row) => (
        <div key={row.label}>
          <div className="flex items-baseline justify-between">
            <span className="text-[#43434a] text-[12.5px]">{row.label}</span>
            <span className="font-semibold text-[#0b0b0d] text-[12.5px]">{row.h}h</span>
          </div>
          <span className="mt-1 block h-2 w-full rounded-full bg-[#fdf3d8]">
            <span
              className="block h-full rounded-full bg-[#a87a18]"
              style={{ width: `${row.w}%` }}
            />
          </span>
        </div>
      ))}
    </div>

    <div className="mt-5 rounded-lg bg-[#fef3e0] p-3">
      <p className="text-[#a87a18] text-[10px] tracking-[0.16em]">{content.hours.exportLabel}</p>
      <div className="mt-1 flex items-center gap-2">
        {content.hours.exportOptions.map((option) => (
          <span className="rounded bg-white px-2 py-1 text-[#43434a] text-[10.5px]" key={option}>
            {option}
          </span>
        ))}
      </div>
    </div>
  </div>
)

export const TimelineSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section className="relative overflow-hidden bg-[#fbf6ec] py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="timeline"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#a87a18] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        <ScrollReveal className="mt-14" delay={100}>
          <TimelineMockup content={content} />
        </ScrollReveal>

        {/* Word-cloud-style band, stages at varying sizes, not a tag grid */}
        <ScrollReveal className="mt-10" delay={120}>
          <div className="rounded-2xl border border-[#ece6d8] bg-white p-8">
            <div className="flex items-baseline justify-between">
              <p className="text-[#a87a18] text-[11px] tracking-[0.2em]">{content.stagesEyebrow}</p>
              <p className="text-[#9a9aa0] text-[11.5px]">{content.stagesNote}</p>
            </div>
            <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-2">
              {content.stages.map((s) => (
                <span className={SIZE_CLASS[s.size]} key={s.label}>
                  {s.label}
                </span>
              ))}
              <span className="text-[#a87a18] text-[12px]">{content.stagesMore}</span>
            </div>
          </div>
        </ScrollReveal>

        {/* Timer (dark) + hours report (light), asymmetric two columns */}
        <div className="mt-8 grid items-stretch gap-6 md:grid-cols-2">
          <ScrollReveal>
            <Timer content={content} />
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <HoursReport content={content} />
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
